import React, { useState, useCallback, useEffect } from 'react';
import { csAdminApi } from '../../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  LinearProgress,
  Paper,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUploadOutlined as UploadIcon,
  DescriptionOutlined as FileIcon,
  CheckCircleOutlined as SuccessIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useLookups } from '../../hooks/useLookups';
import {
  findLookupItem,
  getDocumentTypeRules,
  validateDocumentDates,
} from '../../utils/documentTypeFields';

const CompanyDocumentUploadModal = ({
  open,
  onClose,
  onUpload,
  onUpdate,
  companyId,
  companyName,
  document = null,
  mode = 'create',
  /** When set, type is fixed (upload from document checklist). */
  presetDocumentType = null,
}) => {
  const { items: lookupDocumentTypes } = useLookups('COMPANY_DOCUMENT_TYPE');
  const typeLocked = Boolean(presetDocumentType?.label || presetDocumentType?.code);

  const [documentType, setDocumentType] = useState('');
  const [documentTypeLabel, setDocumentTypeLabel] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = mode === 'edit' && document;
  const selectedType = findLookupItem(lookupDocumentTypes, documentType, documentTypeLabel);
  const fieldRules = getDocumentTypeRules(selectedType);

  const resolveTypeFromPreset = (preset) => {
    if (!preset) return { code: '', label: '' };
    const code = preset.code || preset.label || '';
    const label = preset.label || preset.code || '';
    return { code, label };
  };

  useEffect(() => {
    if (!open) return;

    if (isEditMode && document) {
      setDocumentType(document.typeCode || document.type || '');
      setDocumentTypeLabel(document.typeLabel || document.type || '');
      setDocumentTitle(document.title || '');
      setDocumentNumber(document.number || '');
      setIssueDate(document.issueDate ? new Date(document.issueDate).toISOString().split('T')[0] : '');
      setExpiryDate(document.expiryDate ? new Date(document.expiryDate).toISOString().split('T')[0] : '');
      setNotes(document.notes || '');
      setFile(null);
      return;
    }

    const preset = resolveTypeFromPreset(presetDocumentType);
    setDocumentType(preset.code);
    setDocumentTypeLabel(preset.label);
    setDocumentTitle(preset.label || '');
    setDocumentNumber('');
    setIssueDate('');
    setExpiryDate('');
    setNotes('');
    setFile(null);
    setError(null);
    setSuccess(false);
  }, [open, isEditMode, document, presetDocumentType]);

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles?.length) return;
    const uploadedFile = acceptedFiles[0];

    if (uploadedFile.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      return;
    }

    const validTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!validTypes.includes(uploadedFile.type)) {
      setError('Only PDF, JPG, PNG, DOC, and DOCX files are allowed');
      return;
    }

    setFile(uploadedFile);
    setError(null);
    if (!documentTitle.trim()) {
      setDocumentTitle(uploadedFile.name.replace(/\.[^.]+$/, ''));
    }
  }, [documentTitle]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: false,
    maxSize: 20 * 1024 * 1024,
  });

  const validateForm = () => {
    const category = documentType || documentTypeLabel;
    if (!category) {
      setError('Please select a document type');
      return false;
    }
    if (!documentTitle.trim()) {
      setError('Please enter a document title');
      return false;
    }
    if (!isEditMode && !file) {
      setError('Please select a file to upload');
      return false;
    }
    const dateError = validateDocumentDates({ issueDate, expiryDate, rules: fieldRules });
    if (dateError) {
      setError(dateError);
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!validateForm()) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const category = documentType || documentTypeLabel;
    const lookupCode = findLookupItem(lookupDocumentTypes, documentType, documentTypeLabel)?.code || category;
    const uploadInterval = setInterval(() => {
      setUploadProgress((prev) => (prev >= 100 ? 100 : prev + 10));
    }, 200);

    try {
      if (!isEditMode) {
        if (!file) {
          setError('Please select a file to upload');
          return;
        }
        const formData = new FormData();
        formData.append('file', file);
        formData.append('owner_type', 'COMPANY');
        formData.append('owner_id', companyId ?? '');
        formData.append('company_id', companyId ?? '');
        formData.append('doc_category', lookupCode);
        formData.append('document_name', documentTitle.trim());
        formData.append('documentNumber', documentNumber || '');
        formData.append('issueDate', issueDate || '');
        formData.append('expiryDate', expiryDate || '');
        formData.append('notes', notes || '');
        await csAdminApi.uploadDocument(formData);
      } else {
        const docId = document?.documentId ?? document?.document_id ?? document?.id;
        
        // Better validation for document ID
        if (!docId) {
          setError('Cannot update this document (missing document ID). Please try uploading a new document.');
          return;
        }
        
        // Check for synthetic IDs (row-*)
        if (String(docId).startsWith('row-')) {
          setError('Cannot update this document (invalid document ID). The document may not be saved properly. Please delete and re-upload.');
          return;
        }
        
        // If user selected a new file in edit mode, warn but allow metadata-only update
        if (file) {
          console.warn('CompanyDocumentUploadModal: File replacement in edit mode - updating metadata only', { docId });
          setError('Note: File replacement is not supported in edit mode. Only document metadata will be updated. To replace the file, please use the "Replace" button in the document viewer.');
          // Don't return - allow the metadata update to proceed
        }
        
        await csAdminApi.updateDocument(docId, {
          document_name: documentTitle.trim(),
          doc_category: lookupCode,
          documentNumber,
          issueDate: issueDate || null,
          expiryDate: expiryDate || null,
          notes,
        });
      }

      clearInterval(uploadInterval);
      setUploadProgress(100);
      setSuccess(true);
      setError(null); // Clear any errors on success
      await new Promise((resolve) => setTimeout(resolve, 600));

      const documentData = {
        id: isEditMode ? document.id : Date.now(),
        companyId,
        type: lookupCode,
        typeLabel: documentTypeLabel || category,
        title: documentTitle.trim(),
        number: documentNumber,
        issueDate: issueDate || null,
        expiryDate: expiryDate || null,
        notes,
        fileName: file ? file.name : document?.fileName,
        uploadDate: isEditMode ? document.uploadDate : new Date().toISOString(),
      };

      if (isEditMode && onUpdate) onUpdate(documentData);
      else if (onUpload) onUpload(documentData);

      handleClose();
    } catch (err) {
      console.error('Upload/Update error:', err);
      setError(isEditMode ? 'Update failed. Please try again.' : 'Upload failed. Please try again.');
    } finally {
      clearInterval(uploadInterval);
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setUploadProgress(0);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
  };

  const handleLookupTypeChange = (code) => {
    const match = lookupDocumentTypes.find((t) => t.code === code);
    setDocumentType(code);
    setDocumentTypeLabel(match?.label || code);
    if (!documentTitle.trim() && match?.label) {
      setDocumentTitle(match.label);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" component="div">
              {isEditMode ? 'Edit Document' : 'Upload Company Document'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {companyName}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<SuccessIcon />}>
            {isEditMode ? 'Document updated successfully!' : 'Document uploaded successfully!'}
          </Alert>
        )}

        {typeLocked ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
              Document type
            </Typography>
            <Chip label={documentTypeLabel || documentType} color="primary" variant="outlined" />
          </Box>
        ) : (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Document Type *</InputLabel>
            <Select
              value={documentType}
              label="Document Type *"
              onChange={(e) => handleLookupTypeChange(e.target.value)}
              disabled={uploading}
            >
              {lookupDocumentTypes.map((type) => (
                <MenuItem key={type.code} value={type.code}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          label="Document Title *"
          value={documentTitle}
          onChange={(e) => setDocumentTitle(e.target.value)}
          sx={{ mb: 2 }}
          disabled={uploading}
        />

        <TextField
          fullWidth
          label="Document Number/Reference"
          value={documentNumber}
          onChange={(e) => setDocumentNumber(e.target.value)}
          placeholder="e.g., BR-2025-001, TL-12345"
          sx={{ mb: 2 }}
          disabled={uploading}
          helperText="Optional reference number"
        />

        <TextField
          fullWidth
          label={fieldRules.requiresIssueDate ? 'Issue Date *' : 'Issue Date (optional)'}
          type="date"
          value={issueDate}
          onChange={(e) => setIssueDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
          disabled={uploading}
        />

        <TextField
          fullWidth
          label={fieldRules.requiresExpiry ? 'Expiry Date *' : 'Expiry Date (optional)'}
          type="date"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
          disabled={uploading}
          helperText={
            fieldRules.requiresExpiry
              ? 'Required for this document type'
              : 'Add if this document has an expiry'
          }
        />

        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            mb: 2,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : '#E0E0E0',
            bgcolor: isDragActive ? 'primary.light' : file ? '#E8F5E9' : '#FAFAFA',
            cursor: uploading ? 'not-allowed' : 'pointer',
          }}
        >
          <input {...getInputProps()} disabled={uploading} />
          <Box sx={{ textAlign: 'center' }}>
            {file ? (
              <>
                <FileIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                <Typography variant="body1" fontWeight={500}>{file.name}</Typography>
                <Typography variant="caption" color="textSecondary">{formatFileSize(file.size)}</Typography>
              </>
            ) : isEditMode && document?.fileName ? (
              <>
                <FileIcon sx={{ fontSize: 48, color: '#FF9800', mb: 1 }} />
                <Typography variant="body1" fontWeight={500}>{document.fileName}</Typography>
                <Typography variant="caption" color="textSecondary">Click or drag to replace (optional)</Typography>
              </>
            ) : (
              <>
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" fontWeight={500}>
                  {isDragActive ? 'Drop file here' : 'Drag & drop file here'}
                </Typography>
                <Typography variant="body2" color="textSecondary">or click to browse</Typography>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                  PDF, JPG, PNG, DOC, DOCX (max 20MB)
                </Typography>
              </>
            )}
          </Box>
        </Paper>

        <TextField
          fullWidth
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
          disabled={uploading}
        />

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={
            uploading
            || (!isEditMode && !file)
            || !(documentType || documentTypeLabel)
            || !documentTitle.trim()
          }
          startIcon={uploading ? null : <UploadIcon />}
        >
          {uploading ? (isEditMode ? 'Updating...' : 'Uploading...') : (isEditMode ? 'Update Document' : 'Upload Document')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyDocumentUploadModal;
